// Relatorio "Vendas Campanhas" — vendas dos produtos que estao em promocao (oferta) no ERP.
// Equivalente ao relatorio legado (Relatorios Centrao > Vendas > Promocao > Vendas de Promocao),
// com periodo + promocao + classe combinaveis e quebra por filial.
// Segue a regra 43 (padrao visual do sistema) e a regra 45 (helpers no topo).

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Tooltip from '../components/Tooltip';

const ROXO = '#6B2C5C';
const ROXO_ESCURO = '#2D1427';
const ROXO_CLARO = '#F5EBF2';

// ── helpers puros (regra 45: antes de qualquer coisa que o render invoque) ────
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQtd = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);
const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '');
const iso = (d) => (d ? String(d).slice(0, 10) : '');
const hoje = () => new Date().toISOString().slice(0, 10);
const primeiroDiaDoMes = () => hoje().slice(0, 8) + '01';
const corMkp = (v) => (v === null || v === undefined ? 'text-muted' : v < 0 ? 'text-danger fw-bold' : v < 15 ? 'text-warning' : 'text-success');

export default function VendasCampanhas() {
  // filtros
  const [dataInicio, setDataInicio] = useState(primeiroDiaDoMes());
  const [dataFim, setDataFim] = useState(hoje());
  const [promocoes, setPromocoes] = useState([]);
  const [promosSel, setPromosSel] = useState([]);
  const [promoInput, setPromoInput] = useState('');
  const [promoAberto, setPromoAberto] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classesSel, setClassesSel] = useState([]);
  const [classeInput, setClasseInput] = useState('');
  const [classeAberto, setClasseAberto] = useState(false);
  const [filiais, setFiliais] = useState([]);
  const [filiaisSel, setFiliaisSel] = useState([]);

  // dados
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [expandido, setExpandido] = useState(null);
  const [ordem, setOrdem] = useState({ campo: 'venda', desc: true });
  const [soComVenda, setSoComVenda] = useState(true);
  const [infoAberto, setInfoAberto] = useState(false);

  const promoBoxRef = useRef(null);
  const classeBoxRef = useRef(null);
  const periodoTocado = useRef(false);

  const erro = (msg) => Swal.fire({ icon: 'error', title: 'Ops', text: msg, confirmButtonColor: ROXO });

  useEffect(() => {
    axios.get(`${API}/vendas-campanhas/promocoes`).then((r) => setPromocoes(r.data)).catch(() => setPromocoes([]));
    axios.get(`${API}/vendas-campanhas/classes`).then((r) => setClasses(r.data)).catch(() => setClasses([]));
    axios.get(`${API}/filiais`).then((r) => setFiliais(r.data)).catch(() => setFiliais([]));
  }, []);

  // Regra 33 — dropdowns custom fecham no ESC e no clique fora (mousedown, nao click)
  useEffect(() => {
    function onMouseDown(ev) {
      if (promoBoxRef.current && !promoBoxRef.current.contains(ev.target)) setPromoAberto(false);
      if (classeBoxRef.current && !classeBoxRef.current.contains(ev.target)) setClasseAberto(false);
    }
    function onKey(ev) {
      if (ev.key !== 'Escape') return;
      setPromoAberto(false);
      setClasseAberto(false);
      setInfoAberto(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const promosFiltradas = promocoes.filter((p) => {
    if (promosSel.find((s) => s.oid === p.oid)) return false;
    if (!promoInput.trim()) return true;
    return p.nome.toLowerCase().includes(promoInput.toLowerCase());
  }).slice(0, 60);

  const classesFiltradas = classes.filter((c) => {
    if (classesSel.find((s) => s.clasprod === c.clasprod)) return false;
    if (!classeInput.trim()) return true;
    const q = classeInput.toLowerCase();
    return c.descricao.toLowerCase().includes(q) || c.clasprod.toLowerCase().includes(q);
  }).slice(0, 50);

  // Ao escolher a 1a promocao, o periodo ja vem com a janela dela (editavel — se o
  // usuario mexeu nas datas, respeitamos a escolha dele).
  function addPromo(p) {
    const novas = [...promosSel, p];
    setPromosSel(novas);
    setPromoInput('');
    if (!periodoTocado.current && novas.length === 1) {
      if (p.inicio) setDataInicio(iso(p.inicio));
      if (p.termino) setDataFim(iso(p.termino) > hoje() ? hoje() : iso(p.termino));
    }
  }
  const rmPromo = (p) => setPromosSel((prev) => prev.filter((x) => x.oid !== p.oid));
  const addClasse = (c) => { setClassesSel((prev) => [...prev, c]); setClasseInput(''); };
  const rmClasse = (c) => setClassesSel((prev) => prev.filter((x) => x.clasprod !== c.clasprod));
  const toggleFilial = (cod) => setFiliaisSel((prev) => (prev.includes(cod) ? prev.filter((x) => x !== cod) : [...prev, cod]));

  async function gerar() {
    if (!promosSel.length) return erro('Selecione pelo menos uma promoção.');
    if (!dataInicio || !dataFim) return erro('Informe o período (data inicial e final).');
    if (dataInicio > dataFim) return erro('A data inicial não pode ser maior que a final.');
    setCarregando(true);
    setExpandido(null);
    try {
      const params = {
        promocoes: promosSel.map((p) => p.oid).join(','),
        data_inicio: dataInicio,
        data_fim: dataFim,
      };
      if (classesSel.length) params.classes = classesSel.map((c) => c.clasprod).join(',');
      if (filiaisSel.length) params.filiais = filiaisSel.join(',');
      const r = await axios.get(`${API}/vendas-campanhas/relatorio`, { params });
      setDados(r.data);
    } catch (e) {
      erro(e.response?.data?.error || 'Não foi possível gerar o relatório.');
    } finally {
      setCarregando(false);
    }
  }

  const produtos = useMemo(() => {
    if (!dados) return [];
    let lista = dados.produtos;
    if (soComVenda) lista = lista.filter((p) => p.qtd > 0);
    if (busca.trim()) {
      const q = busca.trim().toLowerCase();
      lista = lista.filter((p) => `${p.codpro} ${p.descricao}`.toLowerCase().includes(q));
    }
    const { campo, desc } = ordem;
    return [...lista].sort((a, b) => {
      const va = a[campo], vb = b[campo];
      if (typeof va === 'string') return desc ? String(vb).localeCompare(va) : String(va).localeCompare(String(vb));
      const na = va === null || va === undefined ? -Infinity : va;
      const nb = vb === null || vb === undefined ? -Infinity : vb;
      return desc ? nb - na : na - nb;
    });
  }, [dados, busca, ordem, soComVenda]);

  const ordenarPor = (campo) => setOrdem((o) => ({ campo, desc: o.campo === campo ? !o.desc : true }));
  const setaDe = (campo) => (ordem.campo === campo ? <i className={`bi bi-caret-${ordem.desc ? 'down' : 'up'}-fill ms-1`} /> : null);

  function exportarCsv() {
    if (!produtos.length) return erro('Nada para exportar.');
    const cab = ['Codigo', 'Descricao', 'Classe', 'Preco promo', 'Preco normal', 'Preco medio', 'Qtd vendida', 'Venda liquida', 'Custo', 'Lucro', 'MKP %'];
    const linhas = produtos.map((p) => [
      p.codpro, `"${(p.descricao || '').replace(/"/g, "'")}"`, `"${p.classe_nome || ''}"`,
      fmt(p.preco_promo), fmt(p.preco_normal), fmt(p.preco_medio),
      fmtQtd(p.qtd), fmt(p.venda), fmt(p.custo), fmt(p.lucro), p.mkp === null ? '' : fmt(p.mkp),
    ].join(';'));
    const csv = '﻿' + [cab.join(';'), ...linhas].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendas-campanhas_${dataInicio}_a_${dataFim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const t = dados?.totais;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0" style={{ color: ROXO_ESCURO }}>
          <i className="bi bi-graph-up me-2" />Vendas Campanhas
        </h4>
        <Tooltip texto="Como este relatório é calculado e no que ele difere do Relatórios Centrão (legado)" largura={300}>
          <button
            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
            style={{ background: ROXO_CLARO, color: ROXO, border: `1px solid ${ROXO}`, width: 28, height: 28, padding: 0 }}
            onClick={() => setInfoAberto(true)}
            aria-label="Sobre este relatório"
          >
            <i className="bi bi-info-lg" />
          </button>
        </Tooltip>
        <span className="text-muted small ms-1">
          Vendas dos produtos em promoção no ERP — período, promoção e classe combináveis.
        </span>
      </div>

      {/* ── filtros ─────────────────────────────────────────────────────────── */}
      <div className="card mb-3" style={{ borderLeft: `4px solid ${ROXO}` }}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3 col-lg-2">
              <label className="form-label small fw-bold">Data inicial</label>
              <input type="date" className="form-control form-control-sm" value={dataInicio}
                onChange={(e) => { periodoTocado.current = true; setDataInicio(e.target.value); }} />
            </div>
            <div className="col-md-3 col-lg-2">
              <label className="form-label small fw-bold">Data final</label>
              <input type="date" className="form-control form-control-sm" value={dataFim}
                onChange={(e) => { periodoTocado.current = true; setDataFim(e.target.value); }} />
            </div>

            <div className="col-md-6 col-lg-4" ref={promoBoxRef} style={{ position: 'relative' }}>
              <label className="form-label small fw-bold">
                Promoções <span className="text-danger">*</span>
                <span className="text-muted fw-normal"> (ofertas do ERP)</span>
              </label>
              <input className="form-control form-control-sm" value={promoInput}
                onChange={(e) => { setPromoInput(e.target.value); setPromoAberto(true); }}
                onFocus={() => setPromoAberto(true)} onClick={() => setPromoAberto(true)}
                placeholder="Clique ou digite para escolher a promoção" />
              {promoAberto && promosFiltradas.length > 0 && (
                <div style={{ position: 'fixed', zIndex: 1100, marginTop: 2, background: '#fff', border: '1px solid #ccc', borderRadius: 4, maxHeight: 280, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 340 }}>
                  {promosFiltradas.map((p) => (
                    <button key={p.oid} type="button" className="btn btn-sm w-100 text-start px-2 py-1"
                      style={{ borderRadius: 0, background: 'transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = ROXO_CLARO)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      onClick={() => addPromo(p)}>
                      <div className="fw-semibold" style={{ fontSize: '.82rem' }}>{p.nome}</div>
                      <div className="text-muted" style={{ fontSize: '.72rem' }}>
                        {fmtData(p.inicio)} a {fmtData(p.termino)} · {p.qtd_produtos} produto(s)
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="col-md-6 col-lg-4" ref={classeBoxRef} style={{ position: 'relative' }}>
              <label className="form-label small fw-bold">
                Classes <span className="text-muted fw-normal">(opcional)</span>
              </label>
              <input className="form-control form-control-sm" value={classeInput}
                onChange={(e) => { setClasseInput(e.target.value); setClasseAberto(true); }}
                onFocus={() => setClasseAberto(true)} onClick={() => setClasseAberto(true)}
                placeholder="Clique ou digite para filtrar por classe" />
              {classeAberto && classesFiltradas.length > 0 && (
                <div style={{ position: 'fixed', zIndex: 1100, marginTop: 2, background: '#fff', border: '1px solid #ccc', borderRadius: 4, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 280 }}>
                  {classesFiltradas.map((c) => (
                    <button key={c.clasprod} type="button" className="btn btn-sm w-100 text-start px-2 py-1"
                      style={{ borderRadius: 0, background: 'transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = ROXO_CLARO)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      onClick={() => addClasse(c)}>
                      <span className="text-muted small me-2">{c.clasprod}</span>{c.descricao}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(promosSel.length > 0 || classesSel.length > 0) && (
            <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
              {promosSel.map((p) => (
                <span key={p.oid} className="badge" style={{ background: ROXO, color: '#fff' }}>
                  <i className="bi bi-tag-fill me-1" />{p.nome}
                  <button type="button" className="btn-close btn-close-white ms-2" style={{ fontSize: '.55em' }} onClick={() => rmPromo(p)} aria-label="remover" />
                </span>
              ))}
              {classesSel.map((c) => (
                <span key={c.clasprod} className="badge" style={{ background: ROXO_CLARO, color: ROXO, border: `1px solid ${ROXO}` }}>
                  {c.descricao}
                  <button type="button" className="btn-close ms-2" style={{ fontSize: '.55em' }} onClick={() => rmClasse(c)} aria-label="remover" />
                </span>
              ))}
              {(promosSel.length > 0 || classesSel.length > 0) && (
                <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                  onClick={() => { setPromosSel([]); setClassesSel([]); }}>limpar</button>
              )}
            </div>
          )}

          <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mt-3">
            <div>
              <label className="form-label small fw-bold d-block mb-1">
                Filiais <span className="text-muted fw-normal">(nenhuma marcada = todas)</span>
              </label>
              <div className="d-flex flex-wrap gap-1">
                {filiais.map((f) => {
                  const cod = String(f.codigo).padStart(2, '0');
                  const on = filiaisSel.includes(cod);
                  return (
                    <button key={f.id || cod} type="button" className="btn btn-sm"
                      style={{
                        background: on ? ROXO : '#fff', color: on ? '#fff' : ROXO,
                        border: `1px solid ${ROXO}`, fontSize: '.75rem', padding: '2px 8px',
                      }}
                      onClick={() => toggleFilial(cod)}>
                      {f.nome}
                    </button>
                  );
                })}
                {filiaisSel.length > 0 && (
                  <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 ms-1" onClick={() => setFiliaisSel([])}>limpar</button>
                )}
              </div>
            </div>
            <button className="btn" style={{ background: ROXO, color: '#fff' }} disabled={carregando} onClick={gerar}>
              {carregando ? <><span className="spinner-border spinner-border-sm me-2" />Gerando…</> : <><i className="bi bi-search me-2" />Gerar relatório</>}
            </button>
          </div>
        </div>
      </div>

      {/* ── resultado ───────────────────────────────────────────────────────── */}
      {!dados && !carregando && (
        <div className="alert" style={{ background: ROXO_CLARO, color: ROXO_ESCURO, border: `1px solid ${ROXO}` }}>
          <i className="bi bi-info-circle me-2" />
          Escolha uma promoção e o período e clique em <strong>Gerar relatório</strong>. O período vem
          preenchido com a janela da promoção, mas você pode recortar qualquer intervalo — inclusive
          combinar várias promoções e filtrar por classe.
        </div>
      )}

      {dados && (
        <>
          <div className="row g-2 mb-3">
            <Card titulo="Venda líquida" valor={`R$ ${fmt(t.venda)}`} icone="bi-cash-stack" destaque />
            <Card titulo="Quantidade vendida" valor={fmtQtd(t.qtd)} icone="bi-box-seam" />
            <Card titulo="Lucro bruto" valor={`R$ ${fmt(t.lucro)}`} icone="bi-graph-up-arrow" />
            <Card titulo="MKP médio" valor={fmtPct(t.mkp)} icone="bi-percent" />
            <Card titulo="Produtos com venda" valor={`${t.produtos_com_venda} de ${t.produtos}`} icone="bi-tags" />
          </div>

          {dados.filiais.length > 0 && (
            <div className="card mb-3">
              <div className="card-body py-2">
                <div className="small fw-bold mb-2" style={{ color: ROXO }}><i className="bi bi-shop me-1" />Por filial</div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: '.82rem' }}>
                    <thead><tr className="text-muted">
                      <th>Filial</th><th className="text-end">Qtd</th><th className="text-end">Venda líquida</th>
                      <th className="text-end">Lucro</th><th className="text-end">MKP</th><th className="text-end">% do total</th>
                    </tr></thead>
                    <tbody>
                      {dados.filiais.map((f) => (
                        <tr key={f.filial}>
                          <td>{f.nome}</td>
                          <td className="text-end">{fmtQtd(f.qtd)}</td>
                          <td className="text-end">R$ {fmt(f.venda)}</td>
                          <td className="text-end">R$ {fmt(f.lucro)}</td>
                          <td className={`text-end ${corMkp(f.mkp)}`}>{fmtPct(f.mkp)}</td>
                          <td className="text-end text-muted">{t.venda > 0 ? fmtPct((f.venda / t.venda) * 100) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <input className="form-control form-control-sm" style={{ maxWidth: 260 }}
                    placeholder="Filtrar por código ou descrição" value={busca} onChange={(e) => setBusca(e.target.value)} />
                  <div className="form-check mb-0">
                    <input className="form-check-input" type="checkbox" id="so-com-venda" checked={soComVenda}
                      onChange={(e) => setSoComVenda(e.target.checked)} style={soComVenda ? { background: ROXO, borderColor: ROXO } : {}} />
                    <label className="form-check-label small" htmlFor="so-com-venda">Só produtos com venda</label>
                  </div>
                  <span className="text-muted small">{produtos.length} produto(s)</span>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={exportarCsv}>
                  <i className="bi bi-filetype-csv me-1" />Exportar CSV
                </button>
              </div>

              <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <table className="table table-sm table-hover align-middle" style={{ fontSize: '.82rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: ROXO_CLARO }}>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th role="button" onClick={() => ordenarPor('codpro')}>Código{setaDe('codpro')}</th>
                      <th role="button" onClick={() => ordenarPor('descricao')}>Produto{setaDe('descricao')}</th>
                      <th>Classe</th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('preco_promo')}>
                        <Tooltip texto="Preço cadastrado na oferta do ERP. Com mais de uma promoção selecionada, mostra o menor." largura={260}>
                          <span>Preço promo{setaDe('preco_promo')}</span>
                        </Tooltip>
                      </th>
                      <th className="text-end">
                        <Tooltip texto="Preço de tabela do produto (PRODUTOCAD.precoven), hoje." largura={240}><span>Preço normal</span></Tooltip>
                      </th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('preco_medio')}>
                        <Tooltip texto="Venda líquida ÷ quantidade líquida — o preço que a loja realmente praticou no período." largura={280}>
                          <span>Preço médio{setaDe('preco_medio')}</span>
                        </Tooltip>
                      </th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('qtd')}>Qtd{setaDe('qtd')}</th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('venda')}>Venda líquida{setaDe('venda')}</th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('custo')}>Custo{setaDe('custo')}</th>
                      <th className="text-end" role="button" onClick={() => ordenarPor('mkp')}>MKP{setaDe('mkp')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos.length === 0 && (
                      <tr><td colSpan={11} className="text-center text-muted py-4">
                        Nenhum produto encontrado com os filtros atuais.
                      </td></tr>
                    )}
                    {produtos.map((p) => {
                      const aberto = expandido === p.codpro;
                      const acima = p.preco_medio > 0 && p.preco_promo > 0 && p.preco_medio > p.preco_promo * 1.005;
                      return (
                        <React.Fragment key={p.codpro}>
                          <tr style={aberto ? { background: ROXO_CLARO } : undefined}>
                            <td>
                              {p.filiais.length > 0 && (
                                <button className="btn btn-sm p-0 border-0 bg-transparent"
                                  onClick={() => setExpandido(aberto ? null : p.codpro)}
                                  aria-label={aberto ? 'Recolher' : 'Ver por filial'}>
                                  <i className={`bi bi-chevron-${aberto ? 'down' : 'right'}`} style={{ color: ROXO }} />
                                </button>
                              )}
                            </td>
                            <td className="text-muted">{p.codpro}</td>
                            <td>{p.descricao}{p.unidade ? <span className="text-muted small ms-1">({p.unidade})</span> : null}</td>
                            <td className="text-muted small">{p.classe_nome}</td>
                            <td className="text-end">{p.preco_promo > 0 ? fmt(p.preco_promo) : '—'}</td>
                            <td className="text-end text-muted">{p.preco_normal > 0 ? fmt(p.preco_normal) : '—'}</td>
                            <td className="text-end">
                              {p.qtd > 0 ? (
                                acima ? (
                                  <Tooltip texto="Preço médio praticado acima do preço da oferta — parte das vendas saiu fora do preço de promoção." largura={280}>
                                    <span className="text-warning fw-semibold">{fmt(p.preco_medio)} <i className="bi bi-exclamation-triangle-fill" /></span>
                                  </Tooltip>
                                ) : fmt(p.preco_medio)
                              ) : '—'}
                            </td>
                            <td className="text-end">
                              {fmtQtd(p.qtd)}
                              {p.devol_qtd > 0 && (
                                <Tooltip texto={`Devolvido no período: ${fmtQtd(p.devol_qtd)} (R$ ${fmt(p.devol_valor)}) — já abatido.`} largura={280}>
                                  <i className="bi bi-arrow-return-left text-danger ms-1" />
                                </Tooltip>
                              )}
                            </td>
                            <td className="text-end fw-semibold">R$ {fmt(p.venda)}</td>
                            <td className="text-end text-muted">R$ {fmt(p.custo)}</td>
                            <td className={`text-end ${corMkp(p.mkp)}`}>{fmtPct(p.mkp)}</td>
                          </tr>
                          {aberto && p.filiais.map((f) => (
                            <tr key={`${p.codpro}-${f.filial}`} style={{ background: '#fbf7fa' }}>
                              <td />
                              <td colSpan={3} className="ps-4 small">
                                <i className="bi bi-shop me-1" style={{ color: ROXO }} />{f.nome}
                              </td>
                              <td colSpan={2} />
                              <td className="text-end small">{f.qtd > 0 ? fmt(f.preco_medio) : '—'}</td>
                              <td className="text-end small">{fmtQtd(f.qtd)}</td>
                              <td className="text-end small">R$ {fmt(f.venda)}</td>
                              <td className="text-end small text-muted">R$ {fmt(f.custo)}</td>
                              <td className={`text-end small ${corMkp(f.mkp)}`}>{fmtPct(f.mkp)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot style={{ position: 'sticky', bottom: 0, background: '#fff' }}>
                    <tr className="fw-bold" style={{ borderTop: `2px solid ${ROXO}` }}>
                      <td colSpan={7} className="text-end">Total {produtos.length !== dados.produtos.length ? '(filtrado)' : ''}</td>
                      <td className="text-end">{fmtQtd(produtos.reduce((s, p) => s + p.qtd, 0))}</td>
                      <td className="text-end">R$ {fmt(produtos.reduce((s, p) => s + p.venda, 0))}</td>
                      <td className="text-end">R$ {fmt(produtos.reduce((s, p) => s + p.custo, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {infoAberto && <ModalInfo onFechar={() => setInfoAberto(false)} />}
    </div>
  );
}

function Card({ titulo, valor, icone, destaque }) {
  return (
    <div className="col-6 col-md-4 col-lg">
      <div className="card h-100" style={destaque ? { borderLeft: `4px solid ${ROXO}` } : undefined}>
        <div className="card-body py-2 px-3">
          <div className="text-muted" style={{ fontSize: '.72rem' }}>
            <i className={`bi ${icone} me-1`} style={{ color: ROXO }} />{titulo}
          </div>
          <div className="fw-bold" style={{ color: ROXO_ESCURO, fontSize: '1.05rem' }}>{valor}</div>
        </div>
      </div>
    </div>
  );
}

// ── Modal "como este relatório é calculado" ──────────────────────────────────
// Pedido explícito do dono: deixar transparente a metodologia e a diferença em
// relação ao Relatórios Centrão (legado), porque os números NÃO batem igual.
function ModalInfo({ onFechar }) {
  const Item = ({ children }) => <li className="mb-1">{children}</li>;
  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3 p-4 mt-3" style={{ maxWidth: 860, width: '100%' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ color: ROXO }}>
            <i className="bi bi-info-circle me-2" />Como este relatório é calculado
          </h5>
          <button className="btn-close" onClick={onFechar} />
        </div>

        <p className="small mb-3">
          Este relatório mostra as <strong>vendas dos produtos que estão em promoção (oferta) no ERP</strong>.
          Ele substitui o <em>Relatórios Centrão → Vendas → Promoção → Vendas de Promoção</em>, mas
          <strong> os números não batem exatamente</strong> com o legado — de propósito. Abaixo, o porquê.
        </p>

        <div className="card mb-3" style={{ borderLeft: `4px solid ${ROXO}` }}>
          <div className="card-body py-2">
            <div className="fw-bold small mb-2" style={{ color: ROXO }}>O que este relatório faz melhor</div>
            <ul className="small mb-0 ps-3">
              <Item><strong>Período livre.</strong> O legado é travado na janela inteira da promoção. Aqui você recorta qualquer intervalo (uma semana, um fim de semana, até ontem).</Item>
              <Item><strong>Promoção, classe e período ao mesmo tempo.</strong> Dá para somar várias promoções, filtrar só uma classe de produto (ex.: só porcelanato) e ainda escolher as filiais.</Item>
              <Item><strong>Quebra por filial.</strong> Cada produto abre a venda loja a loja (a seta à esquerda da linha); o legado não separa.</Item>
              <Item><strong>Preço médio praticado.</strong> Mostra por quanto o produto realmente saiu, comparável ao preço da oferta — quando fica acima, aparece o alerta <i className="bi bi-exclamation-triangle-fill text-warning" />.</Item>
            </ul>
          </div>
        </div>

        <div className="card mb-3" style={{ borderLeft: '4px solid #b8860b' }}>
          <div className="card-body py-2">
            <div className="fw-bold small mb-2" style={{ color: '#8a6508' }}>Por que o valor dá menor que no legado</div>
            <p className="small mb-2">
              O legado soma <code>quantidade × preço</code> bruto. Aqui usamos a <strong>venda líquida</strong>,
              o mesmo cálculo já usado nas campanhas de premiação e no relatório de comissão do sistema:
            </p>
            <ul className="small mb-2 ps-3">
              <Item><strong>Desconto do pedido rateado por item.</strong> O desconto que o vendedor dá no pedido inteiro é distribuído proporcionalmente entre os produtos. O legado ignora isso e infla a venda em cerca de 1%.</Item>
              <Item><strong>Devoluções abatidas.</strong> Nota de devolução (entrada) do período é subtraída da quantidade e do valor. O legado não desconta.</Item>
              <Item><strong>Auto-serviço incluído.</strong> Venda de balcão sem pedido (nota direta) também entra. O legado só enxerga venda com pedido.</Item>
              <Item><strong>Item cancelado fora.</strong> Item cancelado no pedido não conta como venda.</Item>
            </ul>
            <p className="small mb-0 text-muted">
              Na prática a diferença fica em torno de 2% a 3% para menos — e o número daqui é o que
              bate com a realidade do caixa.
            </p>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="fw-bold small mb-2" style={{ color: ROXO }}>Detalhes de cada coluna</div>
            <ul className="small mb-0 ps-3">
              <Item><strong>Preço promo</strong> — preço cadastrado na oferta do ERP. Com mais de uma promoção selecionada (ex.: à vista + 12x), mostra o menor.</Item>
              <Item><strong>Preço normal</strong> — preço de tabela do produto <em>hoje</em>, não o da época da venda.</Item>
              <Item><strong>Preço médio</strong> — venda líquida ÷ quantidade líquida no período.</Item>
              <Item><strong>Qtd</strong> — quantidade líquida (já sem devolução). O ícone <i className="bi bi-arrow-return-left text-danger" /> mostra quanto foi devolvido.</Item>
              <Item><strong>Custo</strong> — custo de compra registrado nas linhas de venda; na devolução, abatido na mesma proporção da quantidade.</Item>
              <Item><strong>MKP</strong> — (venda − custo) ÷ custo, em %. Markup, sempre calculado (regra 5 do sistema).</Item>
            </ul>
          </div>
        </div>

        <div className="alert alert-light border small mb-3">
          <i className="bi bi-clock-history me-1" style={{ color: ROXO }} />
          <strong>Data considerada:</strong> venda com pedido usa a <em>data do pedido</em>; auto-serviço, a
          data da nota; devolução, a data de chegada. Um pedido feito no último dia da promoção e
          faturado depois continua contando no período da promoção.
        </div>

        <div className="text-end">
          <button className="btn" style={{ background: ROXO, color: '#fff' }} onClick={onFechar}>Entendi</button>
        </div>
      </div>
    </div>
  );
}
